import React from "react";
import { useDispatch } from "react-redux";
import axios from "axios";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Paper from "@material-ui/core/Paper";

// jaza soft
import { PageHeader, SimpleForm, TextInput, SelectInput, translate, showNotification } from "jazasoft";

// helper
import useQuery from "../../../hooks/useQuery";
import useMutation from "../../../hooks/useMutation";
import { authServerUrl } from "../../../App.jsx";
import { inputOptions } from "../../../utils/helpers";
import Forbidden from "../../../components/Forbidden";
import { useSetting, useUnit } from "../../../hooks";
import { WarehousFields } from "../../../utils/entityFields";
import getValidation from "../../../utils/getValidation";
import SettingKeys from "../../../utils/SettingKeys";
import { Role } from "../../../utils/types.js";

const useStyles = makeStyles(theme => ({
  container: {
    margin: theme.spacing(3),
    padding: theme.spacing(3)
  }
}));

const hasPrivilege = (roles) => {
  return roles?.includes(Role.MASTER) || roles?.includes(Role.SUPER_USER)
}

const WarehouseEdit = props => {
  const { translate, resource, id, i18nKey, roles } = props;
  const classes = useStyles();

  // api call
  const { data: apis = {} } = useQuery({ url: "apis", params: { appId: "iam" } });
  const { data: warehouse = {} } = useQuery({ url: `warehouses/${id}` });

  const api = apis[0] || {};

  const fields = useSetting(SettingKeys.ENTITY_FIELDS, "warehouses");
  const validationRule = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, "warehouses");

  const dispatch = useDispatch();
  const [mutate] = useMutation();
  const { allUnitList } = useUnit();

  const onSubmit = values => {
    let options = {
      url: `${resource}/${id}`,
      method: "put",
      data: { ...values, unitId: allUnitList?.length === 1 ? allUnitList[0].id : values?.unitId }
    };
    // Update in app first, then in IAM
    mutate(options, {
      onSuccess: async warehouse => {
        options = {
          url: `${authServerUrl}/permissions`,
          method: "put",
          data: { key: "warehouseId", name: warehouse.name, value: warehouse.code },
          headers: { "x-api-key": api?.apiKey }
        };
        const response = await axios.request(options);
        if (response.status === 201 || response.status === 200) {
          dispatch(showNotification("js.notification.updated", { messageArgs: { smart_count: 1, element: "Warehouse" } }));
        }
        props.history.push(`/library/${resource}`);
      },
      onFailure: () => {
        props.history.push(`/library/${resource}`);
      }
    });
  };

  if (!hasPrivilege(roles)) {
    return <Forbidden history={props.history} />;
  }

  return (
    <div>
      <PageHeader title={translate("resources.warehouses.page.edit")} titleArgs={{ _: "Edit Warehouse" }} />
      <Paper className={classes.container}>
        <SimpleForm onSubmit={onSubmit} record={warehouse} i18nKey={i18nKey}>
          {fields.includes(WarehousFields.NAME) && (
            <TextInput
              source="name"
              validate={getValidation(validationRule[WarehousFields.NAME])}
              options={{ ...inputOptions(3), disabled: warehouse && warehouse.isReferenced }}
            />
          )}
          {fields.includes(WarehousFields.CODE) && (
            <TextInput source="code" validate={getValidation(validationRule[WarehousFields.CODE])} {...inputOptions(3)} />
          )}
          {allUnitList?.length > 1 && <SelectInput source="unitId" label="Unit" choices={allUnitList} allowEmpty {...inputOptions(3)} />}
          {fields.includes(WarehousFields.DESC) && (
            <TextInput source="desc" validate={getValidation(validationRule[WarehousFields.DESC])} {...inputOptions(3)} />
          )}
        </SimpleForm>
      </Paper>
    </div>
  );
};

export default translate(WarehouseEdit);
